import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

function reducedNow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(QUERY).matches;
}

/**
 * ¿Puede esta portada moverse?
 *
 * ── Por qué esto no es un detalle ────────────────────────────────────
 * Un vídeo en bucle a pantalla completa es uno de los disparadores
 * clásicos de mareo y migraña para quien tiene sensibilidad vestibular:
 * la imagen se mueve, el cuerpo no, y el desacuerdo entre los dos sentidos
 * es exactamente lo que provoca el malestar. `prefers-reduced-motion` no
 * es una preferencia estética; suele venir de un diagnóstico.
 *
 * Con la preferencia puesta, la portada NO reproduce el vídeo (ni siquiera
 * lo descarga: se pinta el póster como imagen), no hace parallax, no
 * mueve los elementos flotantes, no sigue al ratón y no anima la entrada
 * de las palabras. Queda un cartel fijo. Se ve entero, dice lo mismo y no
 * se mueve nada.
 *
 * ── Se lee de forma síncrona en el primer render ─────────────────────
 * El valor inicial sale de `matchMedia` dentro del inicializador de
 * `useState`, no de un efecto. Si esperáramos al efecto, el primer
 * pintado montaría el `<video>` y arrancaría la animación de entrada
 * durante un fotograma antes de retirarlos: un destello de justo aquello
 * que la persona pidió no ver.
 *
 * `matchMedia` puede no existir (jsdom no lo trae de serie); en ese caso
 * se asume que sí hay movimiento, que es el comportamiento por defecto
 * del navegador cuando nadie ha pedido lo contrario.
 */
export function useHeroMotion(): boolean {
  const [reduced, setReduced] = useState(reducedNow);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(QUERY);

    // La preferencia se puede cambiar con la página abierta (macOS y
    // Windows lo aplican en caliente). Sin este listener, quien la activa
    // para parar el vídeo tendría que recargar para que le hiciera caso.
    const onChange = () => setReduced(query.matches);
    setReduced(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return !reduced;
}
